import { debounce } from 'lodash';
import { useCallback, useState } from 'react';

import { EMedicalCategoryType } from '@/entities/medical-category';
import useRequestDiagnosisV2 from '@/hooks/diagnosis/useRequestDiagnosisV2';
import useCustomRouter from '@/hooks/useCustomRouter';
import useModal from '@/hooks/useModal';
import {
  DiagnosisCategoryType,
  EDiagnosisStep,
  NonFaceToFaceBookingSchedulesInfo,
  TreatmentRequest,
} from '@/models/diagnosis';
import { DiagnosisCategoryInfoModel } from '@/models/diagnosis/category';
import { ROUTES } from '@/types/common';
import { MODAL_BUTTON_TYPE, MODAL_TYPE } from '@/types/modal';
import { addQueryString } from '@/utils/url';
import { useToast } from 'doctornow-design-system';

interface UseTelemedicineRequestSubmissionParams {
  bookingSchedulesInfo: NonFaceToFaceBookingSchedulesInfo;
  categoriesInfo: DiagnosisCategoryInfoModel;
}

export const useTelemedicineRequestSubmission = ({
  bookingSchedulesInfo,
  categoriesInfo,
}: UseTelemedicineRequestSubmissionParams) => {
  const [isRequestDiagnosisLoading, setIsRequestDiagnosisLoading] = useState(false);
  const { showModal, closeModal } = useModal();
  const { router } = useCustomRouter();
  const { mutateRequestDiagnosis } = useRequestDiagnosisV2();
  const { toast } = useToast();

  const moveToCertificationGuide = useCallback(() => {
    router.push(
      `${ROUTES.TELEMEDICINE.REQUEST.path}` +
        addQueryString({
          ...router.query,
          step: EDiagnosisStep.CertificationGuide,
        })
    );
  }, [router]);

  const moveToDiagnosisStep = useCallback(() => {
    router.push(
      `${ROUTES.TELEMEDICINE.REQUEST.path}` +
        addQueryString({
          ...router.query,
          step: EDiagnosisStep.Diagnosis,
        })
    );
  }, [router]);

  const getParsedCategoryType = (categoryType: DiagnosisCategoryType) => {
    return categoryType === DiagnosisCategoryType.Treatment
      ? EMedicalCategoryType.TREATMENT
      : EMedicalCategoryType.DEPARTMENT;
  };

  const getCategoryName = (categoryId: string, categoryType: DiagnosisCategoryType) => {
    return categoriesInfo.getCategoryNameByIdAndType(categoryId, getParsedCategoryType(categoryType));
  };

  const requestDiagnosis = useCallback(
    debounce(
      async (options: {
        skipCertificationCheck?: boolean;
        treatmentRequest: TreatmentRequest;
        isUserNeedVerificationForDiagnosis: boolean;
      }) => {
        const skipCertificationCheck = options.skipCertificationCheck ?? false;

        if (isRequestDiagnosisLoading) {
          return;
        }

        if (!options.treatmentRequest.form.inflowType) {
          toast({ text: '진료 유형을 선택해주세요', direction: 'bottom' });
          return;
        }

        if (!skipCertificationCheck && options.isUserNeedVerificationForDiagnosis) {
          moveToCertificationGuide();
          return;
        }

        setIsRequestDiagnosisLoading(true);
        await mutateRequestDiagnosis(
          {
            request: useRequestDiagnosisV2.parseRequestDiagnosisFromTreatmentRequest(options.treatmentRequest),
          },
          {
            onSuccess: () => {
              const diagnosisDate = bookingSchedulesInfo.getSelectedTimeSlotTitle(options.treatmentRequest);
              const doctorPid = options.treatmentRequest.form.doctorPid;

              if (!doctorPid) {
                return;
              }

              const categoryName = getCategoryName(
                options.treatmentRequest.form.categoryId ?? '',
                options.treatmentRequest.form.categoryType ?? DiagnosisCategoryType.Treatment
              );

              router.replace(
                '/telemedicine/request-complete' +
                  addQueryString({
                    diagnosisDate: diagnosisDate,
                    medicalCategoryName: categoryName,
                    doctorId: doctorPid,
                  })
              );
            },
            onError: (error) => {
              setIsRequestDiagnosisLoading(false);

              showModal({
                type: MODAL_TYPE.ALERT,
                props: {
                  title: error.message,
                  buttons: [
                    {
                      type: MODAL_BUTTON_TYPE.PRIMARY,
                      label: '확인',
                      onClick: () => {
                        closeModal();
                        switch (error.code) {
                          case 'BookingDeadLineExceeded':
                            moveToDiagnosisStep();
                            break;
                          default:
                            break;
                        }
                      },
                    },
                  ],
                  onClickOverlay: closeModal,
                },
              });
            },
          }
        );
      },
      1000,
      {
        leading: true,
        trailing: false,
      }
    ),
    [bookingSchedulesInfo, categoriesInfo] // 최초 호출 및 필수 의존성 추가
  );

  return {
    requestDiagnosis,
    isRequestDiagnosisLoading,
  };
};
